import { useState } from 'react'
import type { CategoryId } from './game/types'
import { CATEGORY_BY_ID } from './game/constants'
import { useTheme } from './hooks/useTheme'
import { LangProvider, useLang } from './i18n/LangContext'
import { LANGS, type Lang } from './i18n/strings'
import { Landing } from './components/Landing'
import { Home } from './components/Home'
import { Game } from './components/Game'
import { Endless } from './components/Endless'
import { ThemeToggle } from './components/ThemeToggle'

/** Optional deep-link: ?cat=nba opens straight to a category (e.g. for blog embeds). */
function initialCategory(): CategoryId | null {
  const c = new URLSearchParams(window.location.search).get('cat')
  return c && c in CATEGORY_BY_ID ? (c as CategoryId) : null
}

function LanguageSwitcher() {
  const { lang, setLang, t } = useLang()
  return (
    <select
      className="lang-select"
      aria-label={t.langLabel}
      value={lang}
      onChange={(e) => setLang(e.target.value as Lang)}
    >
      {LANGS.map((l) => (
        <option key={l.id} value={l.id}>
          {l.label}
        </option>
      ))}
    </select>
  )
}

function AppInner() {
  const { theme, toggle } = useTheme()
  const { t } = useLang()
  const deepLinked = initialCategory()
  const [category, setCategory] = useState<CategoryId | null>(deepLinked)
  // Show the splash first; a ?cat= deep-link (embeds/shares) skips it.
  const [started, setStarted] = useState<boolean>(deepLinked !== null)
  const [endless, setEndless] = useState(false)

  return (
    <div className="lineup app">
      <header className="app-bar">
        <button
          className="wordmark"
          onClick={() => setCategory(null)}
          aria-label="Lineup home"
        >
          <span className="wordmark-over">The</span>
          <span className="wordmark-main">
            Lineup<span className="wordmark-dot">.</span>
          </span>
        </button>
        <div className="app-actions">
          <LanguageSwitcher />
          <ThemeToggle theme={theme} onToggle={toggle} />
        </div>
      </header>

      <main className="screen">
        {category !== null ? (
          <Game category={category} onBack={() => setCategory(null)} />
        ) : endless ? (
          <Endless onBack={() => setEndless(false)} />
        ) : started ? (
          <Home onPick={setCategory} />
        ) : (
          <Landing
            onPlay={() => setStarted(true)}
            onEndless={() => setEndless(true)}
          />
        )}
      </main>

      <footer className="app-foot">
        {t.disclaimer}
        <a
          className="app-foot-credit"
          href="https://hisorty.app"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.inspiredBy}
        </a>
      </footer>
    </div>
  )
}

export default function App() {
  return (
    <LangProvider>
      <AppInner />
    </LangProvider>
  )
}
